import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Container from '../components/layout/Container';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import { useAuth } from '../hooks/useAuth';
import { getCompetitionDetails, getFixtures } from '../services/public-api';
import { generateSchedule } from '../services/bracket-api.js';
import './FixturesCalendarPage.css';

/**
 * FixturesCalendarPage
 * Shows fixtures in an interactive calendar view with date range filtering
 */
const FixturesCalendarPage = () => {
  const { competitionId } = useParams();
  const { isOrganizer } = useAuth();
  const [competition, setCompetition] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('upcoming'); // 'upcoming', 'month', 'custom'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleOpts, setScheduleOpts] = useState({ homeAndAway: false });
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  useEffect(() => {
    fetchCompetition();
  }, [competitionId]);

  useEffect(() => {
    if (selectedDivision) {
      fetchFixtures();
    }
  }, [selectedDivision, viewMode, currentMonth, dateRange]);

  const fetchCompetition = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getCompetitionDetails(competitionId);
      setCompetition(data.competition);
      
      if (data.competition.divisions && data.competition.divisions.length > 0) {
        const firstDivision = data.competition.divisions[0];
        setSelectedDivision(firstDivision.divisionId || firstDivision);
      }
    } catch (err) {
      setError(err.message || 'Failed to load competition');
    } finally {
      setLoading(false);
    }
  };

  const fetchFixtures = async () => {
    if (!selectedDivision) return;

    setLoading(true);
    setError(null);

    try {
      let allFixtures = [];

      if (viewMode === 'upcoming') {
        // Next month from today
        const now = new Date();
        const oneMonthLater = new Date(now);
        oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
        
        const scheduledData = await getFixtures(competitionId, selectedDivision, 'scheduled');
        allFixtures = (scheduledData.matches || []).filter(match => {
          const matchDate = new Date(match.scheduledDate);
          return matchDate >= now && matchDate <= oneMonthLater;
        });
      } else if (viewMode === 'month') {
        // Specific month
        const year = currentMonth.getFullYear();
        const month = String(currentMonth.getMonth() + 1).padStart(2, '0');
        const monthKey = `${year}-${month}`;
        
        const [scheduledData, completedData] = await Promise.all([
          getFixtures(competitionId, selectedDivision, 'scheduled', monthKey),
          getFixtures(competitionId, selectedDivision, 'completed', monthKey)
        ]);
        
        allFixtures = [...(scheduledData.matches || []), ...(completedData.matches || [])];
      } else if (viewMode === 'custom' && dateRange.start && dateRange.end) {
        // Custom date range
        const [scheduledData, completedData] = await Promise.all([
          getFixtures(competitionId, selectedDivision, 'scheduled'),
          getFixtures(competitionId, selectedDivision, 'completed')
        ]);
        
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        end.setHours(23, 59, 59);
        
        allFixtures = [...(scheduledData.matches || []), ...(completedData.matches || [])].filter(match => {
          const matchDate = new Date(match.scheduledDate);
          return matchDate >= start && matchDate <= end;
        });
      }

      allFixtures.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
      setFixtures(allFixtures);
    } catch (err) {
      setError(err.message || 'Failed to load fixtures');
    } finally {
      setLoading(false);
    }
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true,
      });
    }
    
    // Next month days to complete the grid
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }
    
    return days;
  };

  const getFixturesForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return fixtures.filter(f => f.scheduledDate?.startsWith(dateStr));
  };

  const changeMonth = (offset) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + offset);
    setCurrentMonth(newMonth);
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
    setViewMode('month');
  };

  const formatMonthYear = () => {
    return currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatMatchTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const handleGenerateSchedule = async () => {
    setGenerating(true);
    setGenerateResult(null);
    try {
      const result = await generateSchedule(competitionId, scheduleOpts);
      setGenerateResult({ success: true, count: result.matchesCreated });
      setShowScheduleModal(false);
      fetchFixtures(); // refresh
    } catch (err) {
      setGenerateResult({ success: false, error: err.message || 'Failed to generate schedule' });
    } finally {
      setGenerating(false);
    }
  };

  if (loading && !competition) {
    return (
      <Container>
        <Loading size="large" />
      </Container>
    );
  }

  if (error && !competition) {
    return (
      <Container>
        <ErrorMessage message={error} variant="error" onRetry={fetchCompetition} />
      </Container>
    );
  }

  if (!competition) {
    return (
      <Container>
        <div className="not-found">
          <h1>Competition Not Found</h1>
        </div>
      </Container>
    );
  }

  const selectedDateFixtures = selectedDate ? getFixturesForDate(selectedDate) : [];
  const calendarDays = viewMode === 'month' ? getCalendarDays() : [];

  return (
    <Container>
      <div className="fixtures-calendar-page">
        <div className="page-header">
          <Link to={`/competitions/${competitionId}`} className="back-link">
            ← Back to Competition
          </Link>
          <h1>{competition.name} - Fixtures</h1>
          {isOrganizer && competition.competitionType === 'league' && (
            <button
              className="btn btn-primary generate-schedule-btn"
              onClick={() => setShowScheduleModal(true)}
            >
              📅 Generate Schedule
            </button>
          )}
          {generateResult?.success && (
            <div className="success-banner">
              ✅ Schedule generated — {generateResult.count} matches created!
            </div>
          )}
        </div>

        {/* Generate Schedule Modal */}
        {showScheduleModal && (
          <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <h2>Generate Fixture Schedule</h2>
              <p>This will create a round-robin fixture list for all approved teams, spread across weekly match dates.</p>
              {competition.weeklyMatchDay ? (
                <p className="modal-info">
                  📅 Match day: <strong>{competition.weeklyMatchDay}</strong><br />
                  🗓️ Starting from: <strong>{new Date(competition.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                  {competition.breakPeriods?.length > 0 && (
                    <><br />⏸️ Break periods will be skipped automatically</>
                  )}
                </p>
              ) : (
                <p className="modal-warn">⚠️ This competition has no weekly match day set. Edit the competition first to add one.</p>
              )}
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={scheduleOpts.homeAndAway}
                  onChange={e => setScheduleOpts({ ...scheduleOpts, homeAndAway: e.target.checked })}
                />
                Home & away (each team plays each opponent twice)
              </label>
              {generateResult?.success === false && (
                <div className="modal-error">{generateResult.error}</div>
              )}
              <div className="modal-actions">
                <button className="btn btn-outline" onClick={() => setShowScheduleModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleGenerateSchedule}
                  disabled={generating || !competition.weeklyMatchDay}
                >
                  {generating ? 'Generating…' : 'Generate'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Division Selector */}
        {competition.divisions && competition.divisions.length > 0 && (
          <div className="control-group">
            <label>Division:</label>
            <div className="division-tabs">
              {competition.divisions.map((division) => {
                const divisionId = division.divisionId || division;
                const divisionName = division.name || division;
                return (
                  <button
                    key={divisionId}
                    onClick={() => setSelectedDivision(divisionId)}
                    className={`division-tab ${selectedDivision === divisionId ? 'active' : ''}`}
                  >
                    {divisionName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* View Mode Selector */}
        <div className="view-mode-controls">
          <button
            onClick={() => setViewMode('upcoming')}
            className={`view-mode-btn ${viewMode === 'upcoming' ? 'active' : ''}`}
          >
            📅 Upcoming (Next Month)
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`view-mode-btn ${viewMode === 'month' ? 'active' : ''}`}
          >
            📆 Monthly Calendar
          </button>
          <button
            onClick={() => setViewMode('custom')}
            className={`view-mode-btn ${viewMode === 'custom' ? 'active' : ''}`}
          >
            🔍 Custom Range
          </button>
        </div>

        {/* Custom Date Range */}
        {viewMode === 'custom' && (
          <div className="date-range-controls">
            <div className="date-input-group">
              <label>From:</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>
            <div className="date-input-group">
              <label>To:</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
          </div>
        )}

        {/* Month Navigation */}
        {viewMode === 'month' && (
          <div className="month-navigation">
            <button onClick={() => changeMonth(-1)} className="nav-btn">
              ← Previous
            </button>
            <button onClick={goToToday} className="today-btn">
              Today
            </button>
            <h2>{formatMonthYear()}</h2>
            <button onClick={() => changeMonth(1)} className="nav-btn">
              Next →
            </button>
          </div>
        )}

        {loading ? (
          <Loading size="medium" />
        ) : error ? (
          <ErrorMessage message={error} variant="error" onRetry={fetchFixtures} />
        ) : (
          <div className="fixtures-content">
            {viewMode === 'month' ? (
              <div className="calendar-container">
                <div className="calendar-grid">
                  <div className="calendar-header">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="calendar-day-name">{day}</div>
                    ))}
                  </div>
                  <div className="calendar-days">
                    {calendarDays.map((day, index) => {
                      const dayFixtures = getFixturesForDate(day.date);
                      const isToday = day.date.toDateString() === new Date().toDateString();
                      const isSelected = selectedDate?.toDateString() === day.date.toDateString();
                      
                      return (
                        <div
                          key={index}
                          className={`calendar-day 
                            ${!day.isCurrentMonth ? 'other-month' : ''} 
                            ${isToday ? 'today' : ''}
                            ${isSelected ? 'selected' : ''}
                            ${dayFixtures.length > 0 ? 'has-fixtures' : ''}
                          `}
                          onClick={() => dayFixtures.length > 0 && setSelectedDate(day.date)}
                        >
                          <div className="day-number">{day.date.getDate()}</div>
                          {dayFixtures.length > 0 && (
                            <div className="fixture-count">{dayFixtures.length} match{dayFixtures.length !== 1 ? 'es' : ''}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Selected Date Fixtures */}
                {selectedDate && selectedDateFixtures.length > 0 && (
                  <div className="selected-date-fixtures">
                    <h3>
                      Matches on {selectedDate.toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </h3>
                    <div className="fixtures-list">
                      {selectedDateFixtures.map(match => (
                        <div key={match.matchId} className={`fixture-card ${match.status}`}>
                          <div className="match-time">{formatMatchTime(match.scheduledDate)}</div>
                          <div className="match-teams">
                            <div className="team-row">
                              <Link to={`/teams/${match.team1Id}`}>{match.team1Name || 'Team 1'}</Link>
                              {match.status === 'completed' && <span className="score">{match.team1Score}</span>}
                            </div>
                            <div className="versus">vs</div>
                            <div className="team-row">
                              <Link to={`/teams/${match.team2Id}`}>{match.team2Name || 'Team 2'}</Link>
                              {match.status === 'completed' && <span className="score">{match.team2Score}</span>}
                            </div>
                          </div>
                          <div className="match-venue">{match.venue}</div>
                          {match.status === 'completed' && (
                            <Link to={`/matches/${match.matchId}`} className="view-details">
                              View Details →
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* List View for Upcoming/Custom */
              <div className="fixtures-list-view">
                {fixtures.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📅</div>
                    <h3>No Fixtures Found</h3>
                    <p>There are no matches scheduled for this period.</p>
                    {isOrganizer && (
                      <Link to={`/organizer/competitions/${competitionId}/schedule`} className="btn btn-primary">
                        Schedule Matches
                      </Link>
                    )}
                  </div>
                ) : (
                  fixtures.map(match => (
                    <div key={match.matchId} className={`fixture-card-large ${match.status}`}>
                      <div className="fixture-date-time">
                        <div className="date">
                          {new Date(match.scheduledDate).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric'
                          })}
                        </div>
                        <div className="time">{formatMatchTime(match.scheduledDate)}</div>
                      </div>
                      <div className="fixture-details">
                        <div className="match-teams-large">
                          <div className="team-row">
                            <Link to={`/teams/${match.team1Id}`}>{match.team1Name || 'Team 1'}</Link>
                            {match.status === 'completed' && <span className="score">{match.team1Score}</span>}
                          </div>
                          <div className="versus">vs</div>
                          <div className="team-row">
                            <Link to={`/teams/${match.team2Id}`}>{match.team2Name || 'Team 2'}</Link>
                            {match.status === 'completed' && <span className="score">{match.team2Score}</span>}
                          </div>
                        </div>
                        <div className="match-venue">{match.venue}</div>
                      </div>
                      <div className="fixture-actions">
                        {match.status === 'completed' ? (
                          <Link to={`/matches/${match.matchId}`} className="btn-secondary">
                            View Details
                          </Link>
                        ) : (
                          <span className="status-badge">Upcoming</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Container>
  );
};

export default FixturesCalendarPage;
